import { useLlmUsage } from "@/hooks/useLlmUsage";
import type { LlmUsageSummary } from "../hooks/useLlmConfig";
import styles from "./LlmUsagePage.module.css";

function getGaugeColor(pct: number): string {
  if (pct > 0.9) return styles.gaugeRed ?? "";
  if (pct > 0.7) return styles.gaugeOrange ?? "";
  return styles.gaugeGreen ?? "";
}

interface BudgetGaugeProps {
  label: string;
  spent: number;
  budget: number;
}

function BudgetGauge({ label, spent, budget }: BudgetGaugeProps) {
  const pct = budget > 0 ? Math.min(spent / budget, 1) : 0;
  const pctLabel = budget > 0 ? `${Math.round(pct * 100)}%` : "N/A";

  return (
    <div className={styles.gauge}>
      <div className={styles.gaugeLabel}>
        <span>{label}</span>
        <span data-testid={`gauge-pct-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          ${spent.toFixed(2)} / ${budget.toFixed(2)} ({pctLabel})
        </span>
      </div>
      <div className={styles.gaugeTrack}>
        <div
          className={`${styles.gaugeFill} ${getGaugeColor(pct)}`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
    </div>
  );
}

interface ProviderStats {
  calls: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
}

function ProviderBreakdown({ summary }: { summary: LlmUsageSummary }) {
  const entries = Object.entries(summary.byProvider) as [string, ProviderStats][];
  if (entries.length === 0) return null;

  return (
    <div className={styles.providerSection}>
      <h3 className={styles.sectionTitle}>Provider Breakdown</h3>
      <div className={styles.providerGrid}>
        {entries.map(([name, stats]) => (
          <div key={name} className={styles.providerCard}>
            <div className={styles.providerCardName}>{name}</div>
            <div className={styles.providerCardStat}>
              <span>Calls</span>
              <span>{stats.calls}</span>
            </div>
            <div className={styles.providerCardStat}>
              <span>Cost</span>
              <span>${stats.costUsd.toFixed(4)}</span>
            </div>
            <div className={styles.providerCardStat}>
              <span>Tokens</span>
              <span>{(stats.inputTokens + stats.outputTokens).toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LlmUsagePage() {
  const { summary, history, isLoading, page, nextPage, prevPage } = useLlmUsage();

  if (isLoading) {
    return (
      <div className={`${styles.page} form-compact`}>
        <div className={styles.loadingWrap}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!summary && history.length === 0) {
    return (
      <div className={`${styles.page} form-compact`}>
        <div className={styles.header}>
          <h1 className={styles.title}>LLM Usage</h1>
          <p className={styles.subtitle}>Monitor AI-powered OCR costs and performance</p>
        </div>
        <div className={styles.emptyState} data-testid="empty-state">
          No LLM usage recorded yet
        </div>
      </div>
    );
  }

  const totalCalls = summary?.totalCalls ?? 0;
  const successRate =
    totalCalls > 0
      ? `${((summary!.successfulCalls / totalCalls) * 100).toFixed(1)}%`
      : "0%";

  // Avg latency: computed from current history page as a rough indicator
  const avgLatency =
    history.length > 0
      ? Math.round(history.reduce((s, r) => s + r.latencyMs, 0) / history.length)
      : 0;

  return (
    <div className={`${styles.page} form-compact`}>
      <div className={styles.header}>
        <h1 className={styles.title}>LLM Usage</h1>
        <p className={styles.subtitle}>Monitor AI-powered OCR costs and performance</p>
      </div>

      {/* Budget gauges */}
      {summary && (
        <div className={styles.gaugesRow} data-testid="budget-gauges">
          <BudgetGauge
            label="Daily"
            spent={summary.dailySpendUsd}
            budget={5} // from tenant config default; real impl would inject actual budget
          />
          <BudgetGauge
            label="Monthly"
            spent={summary.monthlySpendUsd}
            budget={100}
          />
        </div>
      )}

      {/* Summary cards */}
      <div className={styles.summaryRow} data-testid="summary-cards">
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}>Total Calls</div>
          <div className={styles.summaryCardValue} data-testid="total-calls">
            {totalCalls.toLocaleString()}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}>Success Rate</div>
          <div className={styles.summaryCardValue} data-testid="success-rate">
            {successRate}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}>Avg Latency</div>
          <div className={styles.summaryCardValue} data-testid="avg-latency">
            {avgLatency} ms
          </div>
        </div>
      </div>

      {/* Provider breakdown */}
      {summary && <ProviderBreakdown summary={summary} />}

      {/* Usage history table */}
      <div>
        <h3 className={styles.sectionTitle}>Usage History</h3>
        <table className={styles.historyTable} data-testid="history-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Provider</th>
              <th>Field</th>
              <th>Tokens</th>
              <th>Cost</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {history.map((record) => (
              <tr key={record.id} data-testid="history-row">
                <td>{new Date(record.createdAt).toLocaleString()}</td>
                <td>{record.providerName}</td>
                <td>{record.fieldName ?? record.fieldType ?? "-"}</td>
                <td>{record.inputTokens + record.outputTokens}</td>
                <td>${record.estimatedCostUsd.toFixed(4)}</td>
                <td>
                  <span className={record.success ? styles.statusSuccess : styles.statusFailure}>
                    {record.success ? "OK" : "Fail"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {history.length > 0 && (
          <div className={styles.pagination}>
            <button
              className={styles.paginationBtn}
              onClick={prevPage}
              disabled={page <= 1}
              data-testid="prev-page"
            >
              Prev
            </button>
            <span className={styles.pageLabel}>Page {page}</span>
            <button
              className={styles.paginationBtn}
              onClick={nextPage}
              data-testid="next-page"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
